/**
 * StandardizedTileLayout Component - Universal tile layout for all tile types
 * 
 * This component implements the 6-area standardized layout:
 * 1. Icon (top-left)
 * 2. Title (top-center/right, expandable)
 * 3. Action buttons (top-right)
 * 4. Subtitle/Description (bottom, expandable)
 * 5. Other/Special indicators (center, fixed height)
 * 6. Dividing line (dynamic position)
 * 
 * Maintains all existing responsive sizing rules and dynamic padding.
 */
import React, { useState } from 'react';
import { GridTile as GridTileType } from '../../lib/gridUtils';
import { TileAdaptation } from '../../lib/gridUtils';

interface StandardizedTileLayoutProps {
  tile: GridTileType;
  adaptation: TileAdaptation;
  mode: 'read' | 'edit';
  isSelected: boolean;
  tileIcon: React.ComponentType<{ className?: string }>;
  
  // Content for each area
  titleContent: React.ReactNode;
  actionButtons?: React.ReactNode;
  descriptionContent?: React.ReactNode;
  specialIndicator?: {
    content: React.ReactNode;
    tooltipText?: string;
  };
  
  // Event handlers
  onTileClick?: (e: React.MouseEvent) => void;
  children?: React.ReactNode; // For any additional content
}

export default function StandardizedTileLayout({
  tile,
  adaptation,
  mode,
  isSelected,
  tileIcon: TileIcon,
  titleContent,
  actionButtons,
  descriptionContent,
  specialIndicator,
  onTileClick,
  children
}: StandardizedTileLayoutProps) {
  
  // Hover state for action buttons in read mode
  const [isHovered, setIsHovered] = useState(false);
  
  // Determine if we should show the special indicator area
  const showSpecialArea = specialIndicator && (adaptation.level === 'large' || adaptation.level === 'medium');
  const showTooltipCircle = specialIndicator && (adaptation.level === 'small' || adaptation.level === 'tiny');
  
  // Determine when to show action buttons
  const shouldShowActionButtons = actionButtons && (
    mode === 'edit' || isSelected || (mode === 'read' && isHovered)
  );

  // Smart title line calculation - expand when description is empty/missing
  const hasDescription = descriptionContent && 
    typeof descriptionContent === 'string' ? 
    descriptionContent.trim().length > 0 : 
    Boolean(descriptionContent);
  
  const hasSpecialContent = showSpecialArea || children;
  
  // Calculate dynamic max title lines based on available space
  const calculateMaxTitleLines = () => {
    const baseMaxLines = adaptation.maxTitleLines;
    
    // If no description and no special content, allow title to expand
    if (!hasDescription && !hasSpecialContent) {
      // Allow more expansion based on tile size
      switch (adaptation.level) {
        case 'large': return Math.min(baseMaxLines + 3, 6); // Up to 6 lines for large tiles
        case 'medium': return Math.min(baseMaxLines + 2, 4); // Up to 4 lines for medium tiles  
        case 'small': return Math.min(baseMaxLines + 1, 3); // Up to 3 lines for small tiles
        case 'tiny': return baseMaxLines; // Keep original limit for tiny tiles
        default: return baseMaxLines;
      }
    }
    
    // If description is present but very short, allow modest title expansion
    if (hasDescription && typeof descriptionContent === 'string' && descriptionContent.trim().length < 50) {
      return Math.min(baseMaxLines + 1, adaptation.level === 'large' ? 4 : 3);
    }
    
    // Default to original max lines when description is substantial
    return baseMaxLines;
  };

  const dynamicMaxTitleLines = calculateMaxTitleLines();

  return (
    <div 
      className="relative w-full h-full flex flex-col cursor-pointer"
      style={{ 
        padding: `${adaptation.padding}px`,
        minHeight: '100%'
      }}
      onClick={onTileClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header section: Icon + Title */}
      <div className="flex items-start space-x-2 mb-2">
        {/* Icon - always aligned to top */}
        <div 
          className="flex-shrink-0"
          style={{ color: tile.config?.textColor || '#6b7280' }}
        >
          <TileIcon 
            className={`h-4 w-4 ${tile.type === 'nested_page' ? 'transform rotate-45' : ''}`} 
          />
        </div>
        
        {/* Title - expands dynamically based on available space */}
        <div className="flex-1">
          <h3 
            className={`leading-tight break-words line-clamp-${dynamicMaxTitleLines}`} 
            style={{ 
              color: tile.config?.textColor || '#111827',
              fontSize: tile.config?.fontSize || '14px',
              fontWeight: tile.config?.fontWeight || 'medium',
              fontStyle: tile.config?.fontStyle || 'normal',
              textDecoration: tile.config?.textDecoration || 'none',
              textAlign: tile.config?.headerAlignment || tile.config?.textAlign || 'left',
              transition: 'opacity 0.3s ease'
            }}
          >
            {titleContent}
          </h3>
        </div>
      </div>

      {/* Main content area - flexible */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tile-specific content / Special indicators - vertically centered */}
        <div className="flex-1 flex items-center justify-start overflow-hidden">
          {/* Special indicator area - only show if we have one and enough space */}
          {showSpecialArea && (
            <div className="px-2">
              {specialIndicator?.content}
            </div>
          )}
          {children}
        </div>

        {/* Dividing line - separates main content from bottom description */}
        {adaptation.showDescription && hasDescription && (tile.config?.dividerColor !== 'transparent' && tile.config?.dividerThickness !== '0px') && (
          <div
            style={{
              height: tile.config?.dividerThickness || '1px',
              backgroundColor: tile.config?.dividerColor || '#e5e7eb',
              marginTop: '4px',
              marginBottom: '2px', // Much tighter spacing to description
              opacity: adaptation.descriptionOpacity,
              transition: 'opacity 0.3s ease',
              flexShrink: 0 // Prevent divider from shrinking when description is long
            }}
          />
        )}

        {/* Bottom description area - can expand upward into available space */}
        <div 
          className="flex-1 flex items-end" // Allow expansion upward, align text to bottom
          style={{
            textAlign: tile.config?.descriptionTextAlign || 'center',
            minHeight: 'fit-content', // Don't force minimum height
            // Add padding to the left when tag is present to prevent overlap
            paddingLeft: tile.tag ? '32px' : '0px'
          }}
        >
          {adaptation.showDescription && (
            <div
              className="text-xs w-full"
              style={{
                color: tile.config?.descriptionTextColor || '#9ca3af',
                fontSize: tile.config?.descriptionFontSize || '12px',
                fontWeight: tile.config?.descriptionFontWeight || 'normal',
                fontStyle: tile.config?.descriptionFontStyle || 'normal',
                textDecoration: tile.config?.descriptionTextDecoration || 'none',
                opacity: adaptation.descriptionOpacity,
                transition: 'opacity 0.3s ease',
                lineHeight: '1.2' // Tight line spacing for compact description
                // Removed marginTop - tight spacing from divider
              }}
            >
              {descriptionContent}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons - positioned outside shape clipping, matching unified layout */}
      {shouldShowActionButtons && (
        <div 
          className="absolute top-2 right-2 flex space-x-1 opacity-100 transition-opacity z-40"
          style={{ 
            clipPath: 'none',
            pointerEvents: 'auto'
          }}
        >
          {actionButtons}
        </div>
      )}
      
      {/* SMALL/TINY TOOLTIP CIRCLE (bottom-right) */}
      {showTooltipCircle && (
        <div 
          className="absolute bottom-2 right-2 w-3 h-3 bg-yellow-400 rounded-full cursor-help"
          title={specialIndicator?.tooltipText || ''}
          style={{ zIndex: 25 }}
        />
      )}
      
      {/* TILE TAG (bottom-left) */}
      {tile.tag && (
        <div 
          className="absolute bottom-2 left-2 text-lg select-none pointer-events-none"
          style={{ zIndex: 25 }}
        >
          {tile.tag}
        </div>
      )}
      
      {/* Additional content area for tile-specific elements */}
      {children}
    </div>
  );
}

